import { useState } from 'react';
import { DialogConfirmar } from '../../../components/admin/dialog-confirmar';
import { formatarBRL } from '../../../lib/formato';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  useAdminAtualizarTaxaKmMutation,
  useAdminCriarTaxaKmMutation,
  useAdminListarTaxasKmQuery,
  useAdminRemoverTaxaKmMutation,
  type TaxaEntregaKmLoja,
} from '../../../api';

/**
 * Faixas de distância da taxa de entrega.
 *
 * Cada linha é um teto: "até 3 km, R$ 5". No checkout, a distância do endereço
 * cai na primeira faixa que a cobre; **passou da última faixa, a entrega é
 * recusada** — é assim que a loja delimita a área de entrega, e é por isso que
 * a tela avisa qual é o alcance atual.
 */



interface Editor {
  modo: 'criar' | 'editar';
  faixa?: TaxaEntregaKmLoja;
  kmFinal: string;
  valor: string;
  ativo: boolean;
}

export function FaixasKm({ idConfig }: { idConfig: number }): JSX.Element {
  const { data, isLoading } = useAdminListarTaxasKmQuery({ idConfig, pagina: 1, tamanhoPagina: 100 });
  const [criar, statusCriar] = useAdminCriarTaxaKmMutation();
  const [atualizar, statusAtualizar] = useAdminAtualizarTaxaKmMutation();
  const [remover] = useAdminRemoverTaxaKmMutation();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  /** Faixa aguardando confirmação de remoção — `null` = nenhuma. */
  const [removendo, setRemovendo] = useState<{ id: number; kmFinal: string } | null>(null);

  const faixas = [...(data?.itens ?? [])].sort((a, b) => Number(a.kmFinal) - Number(b.kmFinal));
  const ativas = faixas.filter((f) => f.ativo);
  const alcance = ativas.length ? Number(ativas[ativas.length - 1]?.kmFinal ?? 0) : 0;
  const salvando = statusCriar.isLoading || statusAtualizar.isLoading;

  const abrirCriar = (): void =>
    setEditor({ modo: 'criar', kmFinal: '', valor: '', ativo: true });

  const abrirEditar = (faixa: TaxaEntregaKmLoja): void =>
    setEditor({
      modo: 'editar',
      faixa,
      kmFinal: String(Number(faixa.kmFinal)),
      valor: String(Number(faixa.valor)),
      ativo: faixa.ativo,
    });

  const salvar = async (): Promise<void> => {
    if (!editor) return;
    const kmFinal = Number(editor.kmFinal.replace(',', '.'));
    const valor = Number(editor.valor.replace(',', '.'));
    if (!Number.isFinite(kmFinal) || kmFinal <= 0) return setErro('Informe a distância em KM (maior que zero).');
    if (!Number.isFinite(valor) || valor < 0) return setErro('Informe o valor da faixa.');

    // Duas faixas com o mesmo teto deixariam o resultado dependendo da ordem do
    // banco — barra aqui, que é onde dá para explicar.
    const repetida = faixas.some(
      (f) => f.id !== editor.faixa?.id && Number(f.kmFinal) === kmFinal && f.ativo,
    );
    if (repetida) return setErro(`Já existe uma faixa ativa até ${kmFinal} km.`);

    setErro(null);
    try {
      if (editor.modo === 'criar') {
        await criar({ idConfiguracaoCardapio: idConfig, kmFinal, valor, ativo: editor.ativo }).unwrap();
      } else if (editor.faixa) {
        await atualizar({ id: editor.faixa.id, dados: { kmFinal, valor, ativo: editor.ativo } }).unwrap();
      }
      setEditor(null);
    } catch (err) {
      // Sem isto o diálogo ficava aberto e mudo: o gestor clica de novo achando
      // que não pegou, e nada explica o que houve.
      setErro(
        (err as { data?: { mensagem?: string } })?.data?.mensagem ??
          'Não foi possível salvar a faixa. Tente novamente.',
      );
    }
  };

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Typography color="text.secondary" variant="body2">
          {ativas.length === 0
            ? 'Nenhuma faixa cadastrada — o cliente não consegue fechar pedido de entrega.'
            : `Entrega até ${alcance.toString().replace('.', ',')} km da loja. Endereço além disso é recusado no checkout.`}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCriar}>
          Nova faixa
        </Button>
      </Stack>

      {ativas.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cadastre pelo menos uma faixa. Sem faixa, todo endereço fica fora da área de entrega.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table sx={{ minWidth: 480 }}>
            <TableHead>
              <TableRow>
                <TableCell>Distância</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faixas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhuma faixa cadastrada.
                  </TableCell>
                </TableRow>
              ) : (
                faixas.map((faixa, indice) => {
                  const anterior = indice > 0 ? Number(faixas[indice - 1]?.kmFinal ?? 0) : 0;
                  return (
                    <TableRow key={faixa.id} sx={{ opacity: faixa.ativo ? 1 : 0.5 }}>
                      <TableCell>
                        {anterior > 0 ? `de ${anterior} a ${Number(faixa.kmFinal)} km` : `até ${Number(faixa.kmFinal)} km`}
                      </TableCell>
                      <TableCell align="right">{formatarBRL(faixa.valor)}</TableCell>
                      <TableCell>{faixa.ativo ? 'Ativa' : 'Inativa'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEditar(faixa)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton size="small" onClick={() => setRemovendo({ id: faixa.id, kmFinal: faixa.kmFinal })}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={!!editor} onClose={() => setEditor(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editor?.modo === 'criar' ? 'Nova faixa' : 'Editar faixa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {erro && <Alert severity="error">{erro}</Alert>}
            <TextField
              label="Distância máxima (km)"
              value={editor?.kmFinal ?? ''}
              onChange={(e) => setEditor((a) => (a ? { ...a, kmFinal: e.target.value } : a))}
              helperText="Teto da faixa. Ex.: 3 cobre de 0 a 3 km."
              size="small"
              autoFocus
            />
            <TextField
              label="Valor da entrega (R$)"
              value={editor?.valor ?? ''}
              onChange={(e) => setEditor((a) => (a ? { ...a, valor: e.target.value } : a))}
              size="small"
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                checked={editor?.ativo ?? true}
                onChange={(e) => setEditor((a) => (a ? { ...a, ativo: e.target.checked } : a))}
              />
              <Typography variant="body2">Faixa ativa</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditor(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogConfirmar
        aberto={removendo !== null}
        titulo="Remover esta faixa de KM?"
        descricao={
          removendo
            ? `A faixa até ${removendo.kmFinal} km deixa de calcular taxa. Pedidos nessa distância passam a cair na faixa seguinte, se houver.`
            : undefined
        }
        textoConfirmar="Remover"
        aoCancelar={() => setRemovendo(null)}
        aoConfirmar={() => {
          if (removendo) void remover(removendo.id);
          setRemovendo(null);
        }}
      />
    </>
  );
}
